import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useAiPrintoutData } from "../../../hooks/useAiPrintoutData";
import { useCustomPrintoutDetail } from "../../../hooks/useCustomPrintouts";
import { useIsReadOnlyForDepartment } from "../../../hooks/useIsReadOnlyForDepartment";
import { PRINTOUT_PANE_GAP_PX, PRINTOUT_SETTINGS_WIDTH_PX } from "../printoutLayout";
import { CustomPrintoutDialogs } from "./CustomPrintoutDialogs";
import { CustomPrintoutPanel } from "./CustomPrintoutPanel";
import { CustomPrintoutPreview } from "./CustomPrintoutPreview";
import { CustomPrintoutVersionBar } from "./CustomPrintoutVersionBar";
import { CustomPrintoutVersionHistory } from "./CustomPrintoutVersionHistory";
import { PRINT_TITLE_DATE_FORMAT, STACKED_DESIGN_PANE_SHARE } from "./consts";
import { useCustomPrintoutActions } from "./useCustomPrintoutActions";
import { useCustomPrintoutDraft } from "./useCustomPrintoutDraft";
import { useCustomPrintoutSelection } from "./useCustomPrintoutSelection";
import { useCustomPrintoutVersionActions, useViewedCustomPrintoutVersion } from "./useCustomPrintoutVersionView";
import { useLeaveGuard } from "./useLeaveGuard";

type Props = {
	date: DateTime;
	showActual: boolean;
	logPrintout: (action: "download" | "print", eventParams: Record<string, unknown>) => void;
};

export type CustomPrintoutContainerHandle = {
	/** Runs `onLeave` now, or once the user agrees to lose an unsaved design; `onStay` if they keep it. */
	confirmLeave: (onLeave: () => void, onStay?: () => void) => void;
};

/**
 * The Custom mode of the Download Day view popup: the design pane on the left, the sandboxed preview on the
 * right. Owns which printout is shown — and which of its versions: the current one, or an earlier one picked from
 * its history — the draft being designed for it, and the dialogs around saving, renaming, deleting, restoring and
 * leaving with unsaved work. The popup asks it before closing or switching mode (`confirmLeave`).
 */
export const CustomPrintoutContainer = forwardRef<CustomPrintoutContainerHandle, Props>(({ date, showActual, logPrintout }, ref) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const canEdit = !useIsReadOnlyForDepartment();
	const dataState = useAiPrintoutData(date, showActual);
	const selection = useCustomPrintoutSelection();
	const { printouts, printoutId, select, startFromId } = selection;
	const detailQuery = useCustomPrintoutDetail(printoutId);
	const startFromQuery = useCustomPrintoutDetail(startFromId);
	const viewed = useViewedCustomPrintoutVersion(printoutId);
	// An earlier version on screen replaces the current one everywhere the saved printout is read: preview and chat.
	// A new printout started from a saved one shows that printout's template instead.
	const shownQuery = viewed.viewedVersionId === null ? detailQuery : viewed.versionQuery;
	const sourceQuery = printoutId === null && startFromId !== null ? startFromQuery : shownQuery;
	const startFrom = startFromId === null ? null : { printoutId: startFromId, detail: startFromQuery.data === undefined ? null : startFromQuery.data };
	const draft = useCustomPrintoutDraft({ printoutId, saved: shownQuery.data === undefined ? null : shownQuery.data, startFrom, data: dataState.data });
	const selected = printouts.find((printout) => printout.id === printoutId);
	const startedFrom = printouts.find((printout) => printout.id === startFromId);
	const startNewFrom = useStartNewFrom(selection.startFrom);
	const actions = useCustomPrintoutActions({ selection, draft, selected });
	const leaveGuard = useLeaveGuard(draft.isDirty, draft.discard);
	const versions = useCustomPrintoutVersionActions({ printoutId, viewed, guard: leaveGuard.guard, discard: draft.discard });
	useImperativeHandle(ref, () => ({ confirmLeave: leaveGuard.guard }), [leaveGuard.guard]);
	const { viewedVersionNumber, viewedSummary } = versions;

	return (
		<Box
			data-testid="custom-printout-container"
			sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: `${PRINTOUT_PANE_GAP_PX}px` } }}
		>
			<Box
				sx={{
					flex: { xs: canEdit ? `0 0 ${STACKED_DESIGN_PANE_SHARE}` : "0 0 auto", md: `0 0 ${PRINTOUT_SETTINGS_WIDTH_PX}px` },
					minHeight: 0,
					minWidth: 0,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<CustomPrintoutPanel
					listStatus={selection.listQuery.status}
					printouts={printouts}
					selectedId={printoutId}
					canEdit={canEdit}
					draft={draft}
					isDataReady={!selection.isResolving && dataState.data !== null && (startFrom === null || startFrom.detail !== null)}
					isSaving={actions.isSaving}
					onSelect={(id) => leaveGuard.guard(() => select(id))}
					onNew={() => leaveGuard.guard(() => select(null))}
					onNewFromSelected={() => {
						if (selected !== undefined) leaveGuard.guard(() => startNewFrom(selected.id));
					}}
					startedFromName={startedFrom === undefined ? null : startedFrom.name}
					onRename={actions.openRename}
					onDelete={actions.openDelete}
					onRetryList={() => void selection.listQuery.refetch()}
					onSave={actions.save}
					onDiscard={actions.askDiscard}
					versionHistory={
						selected === undefined ? null : (
							<CustomPrintoutVersionHistory
								versions={versions}
								shownVersionNumber={viewedVersionNumber === null ? selected.versionNumber : viewedVersionNumber}
								viewedVersionId={viewed.viewedVersionId}
							/>
						)
					}
					viewingVersionNumber={viewedVersionNumber}
				/>
			</Box>
			<CustomPrintoutPreview
				dataState={dataState}
				source={selection.isResolving || sourceQuery.isError ? null : draft.previewSource}
				sourceFailed={sourceQuery.isError}
				onRetrySource={() => void sourceQuery.refetch()}
				report={draft.report}
				onReport={draft.onReport}
				phase={draft.phase}
				isDraft={draft.draft !== null}
				canDesign={canEdit}
				// An earlier version can't be redesigned in place: it is restored first, then refined.
				canFix={viewedVersionNumber === null && draft.canFix}
				onFix={draft.fixWithAnnie}
				printoutId={printoutId}
				coverage={draft.coverage}
				printTitle={t("printout.custom.ai.print.title", {
					department: chosenDepartment.display_name,
					name: selected === undefined ? t("printout.custom.ai.new.name") : selected.name,
					date: date.toFormat(PRINT_TITLE_DATE_FORMAT),
				})}
				logPrintout={logPrintout}
				viewedVersionNumber={viewedVersionNumber}
				versionBar={
					viewedSummary !== undefined &&
					selected !== undefined && (
						<CustomPrintoutVersionBar
							version={viewedSummary}
							currentVersionNumber={selected.versionNumber}
							canRestore={canEdit}
							isRestoring={versions.isRestoring}
							onRestore={versions.askRestore}
							onBackToCurrent={versions.backToCurrent}
						/>
					)
				}
			/>
			<CustomPrintoutDialogs
				actions={actions}
				leaveGuard={leaveGuard}
				versions={versions}
				printouts={printouts}
				printoutId={printoutId}
				isGenerating={draft.isGenerating}
			/>
		</Box>
	);
});
CustomPrintoutContainer.displayName = "CustomPrintoutContainer";

/** Starts a new printout from a saved one, and records that it did. */
const useStartNewFrom = (startFrom: (printoutId: string) => void) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	return (printoutId: string): void => {
		track("custom_printout_new_from_saved", { department_id: chosenDepartment.department_id });
		startFrom(printoutId);
	};
};
